import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../api/client';

interface WorkspaceMembership {
  id: string;
  slug: string;
  name: string;
  logo_url?: string;
  role: string;
}

interface User {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  onboarding_completed?: number;
  workspaces?: WorkspaceMembership[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, full_name?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  exchangeSSOToken: (ssoToken: string) => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  fetchMe: () => Promise<void>;
  currentWorkspace: WorkspaceMembership | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

function getWorkspaceIdFromToken(jwt: string | null): string | null {
  if (!jwt) return null;
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json.workspace_id || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(getWorkspaceIdFromToken(token));

  const currentWorkspace = user?.workspaces?.find((w) => w.id === currentWorkspaceId) || null;

  const syncToken = useCallback((newToken: string) => {
    localStorage.setItem('token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    setCurrentWorkspaceId(getWorkspaceIdFromToken(newToken));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    api.setToken(null);
    setToken(null);
    setUser(null);
    setCurrentWorkspaceId(null);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get('/api/me');
      if (res.data) setUser(res.data);
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      fetchMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const res = await api.post('/api/auth/switch-workspace', { workspace_id: workspaceId });
    syncToken(res.data.token);
    await fetchMe();
  }, [fetchMe, syncToken]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post('/api/auth/login', { email, password });
    syncToken(res.data.token);
    await fetchMe();
  }, [fetchMe, syncToken]);

  const register = useCallback(async (email: string, password: string, full_name?: string) => {
    const res = await api.post('/api/auth/register', { email, password, full_name });
    syncToken(res.data.token);
    await fetchMe();
  }, [fetchMe, syncToken]);

  const exchangeSSOToken = useCallback(async (ssoToken: string) => {
    const res = await api.post('/api/auth/sso/exchange', { token: ssoToken });
    syncToken(res.data.token);
    await fetchMe();
  }, [fetchMe, syncToken]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register, loading, exchangeSSOToken, switchWorkspace, fetchMe, currentWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
